<template>
    <v-container
            id="products"
            fluid
            tag="section"
    >

        <ProductsFilters @onFiltersChanged="onFiltersChanged"/>

        <p v-if="!loading && !products.length"
           class="text-center">
            {{ $t('products.no_product') }}
        </p>

        <p v-if="loading && !products.length"
           class="text-center">
            <v-progress-circular
                    class="mb-4"
                    indeterminate
                    color="primary"
            ></v-progress-circular>
            <br/>
            {{ $t('products.loading') }}
        </p>

        <v-row
                id="list-products"
                align="stretch"
                justify="center"
        >

            <v-col
                    cols="12"
                    xl="3"
                    md="4"
                    sm="6"
                    v-for="product in products"
                    :key="product.id"
            >
                <v-card elevation="1">
                    <a :href="`products/${product.id}`">
                        <v-img v-if="isDisabled(product)"
                               height="200px"
                               :src="product.images[0]"
                               class="hidden-for-sale"
                               contain
                        ></v-img>
                        <v-img v-else
                               height="200px"
                               :src="product.images[0]"
                               class="align-end"
                               contain
                        ></v-img>
                    </a>

                    <v-card-title
                        :title="product.title"
                        v-text="product.title">
                    </v-card-title>

                    <v-card-text>
                        <v-row>
                            <v-col cols="8">
                                <div class="text-subtitle-1 pb-5">
                                    {{product.reference}}
                                </div>
                            </v-col>
                            <v-col cols="4">
                                <div class="text-h6 pb-5 text-right">
                                    <template v-if="product.price !== product.discount_price && product.discount_price">
                                        <span class="text-subtitle-1 font-weight-light">{{ product.discount_rate | toPercent }} - <del>{{ $n(product.price , 'currency') }}</del></span>
                                        <br/>
                                        <span class="pink--text text--darken-2">{{ $n(product.discount_price , 'currency') }}</span>
                                    </template>
                                    <template v-else>
                                        {{ $n(product.price , 'currency') }}
                                    </template>
                                </div>
                            </v-col>
                        </v-row>
                        <v-row v-if="product.discount_rate_proposal">
                            <v-container fluid>
                                <v-card color="#f9dbc2">
                                    <v-card-title class="text-center">Suggestion de soldes
                                        <v-spacer></v-spacer>
                                        <v-tooltip left top>
                                            <template v-slot:activator="{ on, attrs }">
                                                    <v-icon v-bind="attrs"
                                                            v-on="on">mdi-information</v-icon>
                                            </template>
                                            <span>Opération Archives du {{ product.discount_start_datetime | momentDateSimple }} au {{ product.discount_end_datetime | momentDateSimple }}</span>
                                        </v-tooltip>

                                    </v-card-title>
                                    <v-card-text class="text-center font-weight-medium">
                                        <v-row>
                                            <v-col cols="6" md="6">
                                                <v-select
                                                    v-model="product.discount_rate_proposal"
                                                    :items="discountRatesList"
                                                    item-text="label"
                                                    item-value="value"
                                                    label="Réduction"
                                                    hide-details
                                                    prepend-icon="sale"
                                                    single-line
                                                    @change="calculateFDiscountPriceFromDiscountRate(product.discount_rate, product)"
                                                ></v-select>
                                            </v-col>
                                            <v-col cols="6" md="6" class="pink--text text--darken-2">
                                                soit {{ calculateFDiscountPriceFromDiscountRate(product.discount_rate_proposal, product.price) | toCurrency }}
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                    <v-card-actions>
                                        <v-spacer></v-spacer>
                                        <v-btn icon
                                               @click="dismissDiscountOnProduct(product)"
                                        >
                                            <v-icon color="red darken-2" x-large>mdi-close-circle</v-icon>
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                        <v-btn icon
                                            @click="applyDiscountOnProduct(product)"
                                        >
                                            <v-icon color="green darken-2" x-large>mdi-check-circle</v-icon>
                                        </v-btn>
                                        <v-spacer></v-spacer>
                                    </v-card-actions>
                                </v-card>
                            </v-container>
                        </v-row>
                        <v-row>
                            <v-chip
                                class="ma-2 mt-0"
                                :class="getStatusColor(product.status)"
                            >
                                {{getStatusLabel(product.status)}}
                            </v-chip>
                            <v-chip
                                class="ma-2 mt-0"
                                v-if="product.cancelled === true"
                            >
                                Annulée
                            </v-chip>
                            <v-chip
                                class="ma-2 mt-0"
                                v-if="product.status === 'REJECTED'"
                            >
                                {{ $t('products.rejected.reason-' + product.rejection_reason_type)}}
                            </v-chip>

                            <v-chip v-if="product.sold_at"
                                    class="ma-2 mt-0"
                                    outlined
                            >
                                {{ product.sold_at | momentDateSimple}}
                            </v-chip>
                            <v-chip v-if="product.paid"
                                    class="ma-2 mt-0"
                            >
                                Payé
                            </v-chip>
                            <v-chip v-if="product.returned"
                                    class="ma-2 mt-0"
                            >
                                <template v-if="product.return_type === 'warehouse'">
                                    Retour vers Atelier
                                </template>
                                <template v-else-if="product.return_type === 'store'">
                                    Retour vers Vendeur
                                </template>
                                <template v-else>
                                    Retournée
                                </template>
                            </v-chip>
                            <v-chip
                                outlined
                                class="ma-2 mt-0"
                                :title="$t('products.in_imparfaite_warehouse')"
                                v-if="product.in_imparfaite_warehouse"
                            >
                                <v-icon v-bind="attrs"
                                        v-on="on"
                                        color="red accent-3">mdi-warehouse</v-icon>
                            </v-chip>
                        </v-row>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                       <v-btn icon
                              :href="'https://www.imparfaiteparis.com?post_type=product&p='+ product.woo_commerce_product_id"
                              target="_blank"
                              title="Voir ce produit sur Imparfaite"
                       >
                           <v-icon>mdi-open-in-new</v-icon>
                       </v-btn>

                        <v-spacer></v-spacer>
                        <v-menu bottom left v-if="product.editable || product.fully_editable">
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    icon
                                    v-on="on"
                                >
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    :href="'https://www.imparfaiteparis.com?post_type=product&p='+ product.woo_commerce_product_id"
                                    target="_blank"
                                >
                                    <v-list-item-title>{{ $t('global.view_on_imparfaite') }}
                                    </v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    :to="'products/' + product.id"
                                >
                                    <v-list-item-title>{{ $t('product.edit') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    @click="deleteProduct(product.id)"
                                >
                                    <v-list-item-title class="red--text">{{ $t('product.delete') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <div class="text-center">
            <v-container>
                <v-row justify="center">
                    <v-col cols="8">
                        <v-container class="max-width">
                            <v-pagination
                                    v-model="options.page"
                                    class="my-4"
                                    :length="countPage"
                                    total-visible="10"
                            ></v-pagination>
                        </v-container>
                    </v-col>
                </v-row>
            </v-container>
        </div>

        <v-snackbar
                v-model="snackbar"
                :color="color"
        >
            {{ message }}
            <v-btn
                    dark
                    text
                    @click="snackbar = false"
            >
                {{ $t('close') }}
            </v-btn>
        </v-snackbar>

    </v-container>
</template>

<script>
    import mappingStatusColors from '../../../config/mapping-status-colors'
    import {mapState} from 'vuex'
    import {createQueryString} from '@/helpers/createQueryString'
    import ProductsFilters from '../components/ProductsFilters'

    export default {
        name: 'Products',

        beforeMount() {
            this.load();
        },

        data() {
            return {
                discountRatesList: [
                    {'label': '-10%', 'value': 10},
                    {'label': '-20%', 'value': 20},
                    {'label': '-30%', 'value': 30},
                    {'label': '-40%', 'value': 40},
                    {'label': '-50%', 'value': 50},
                ],
                snackbar: false,
                color: 'info',
                message: '',
                loading: false,
                products: [],
                totalItems: 0,
                filters: {},
                options: {
                    page: 1,
                    itemsPerPage: 30,
                },
            };
        },

        methods: {
            onFiltersChanged(filters) {
                this.filters = filters;
                this.load();
            },
            load() {
                this.loading = true;
                this.products = [];
                let params = {
                    page: this.options.page, ...this.$route.params, ...this.$route.query, ...this.filters,
                    order: {}
                };

                if (this.queryStatus && this.queryStatus.includes('SOLD')) {
                    params.order['sold_at'] = 'desc';
                }

                this.$axios.get('products?' + createQueryString(params))
                    .then(response => {
                        this.loading = false;
                        this.products = response.data['hydra:member'];
                        this.totalItems = response.data['hydra:totalItems'];
                    });
            },
            getStatusLabel(status) {
                return this.$i18n.t('products.labels.' + status.toLowerCase())
            },
            getStatusColor(status) {
                return mappingStatusColors[status];
            },
            hasRole(role) {
                return this.userInformation.roles.includes(role);
            },
            isDisabled(product) {
                return product.status === 'HIDDEN_FOR_SALE';
            },

            deleteProduct(productId) {
                this.$axios.delete('products/' + productId)
                    .then(() => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.product_deleted');
                        this.color = 'success';
                        this.load();
                    })
                    .catch(() => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.product_delete_failed');
                        this.color = 'error';
                        this.load();
                    });
            },
            calculateFDiscountPriceFromDiscountRate(discountRate, price) {
                return price - (price * discountRate / 100);
            },
            applyDiscountOnProduct(product) {
                let itemIndex = this.products.indexOf(product)
                this.$axios.post('products/' + product.id + '/discounts/applies', {
                    'discount_rate' : null,
                    'discount_rate_proposal' : product.discount_rate_proposal
                })
                    .then((response) => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.product_saved');
                        this.color = 'success';
                        this.products.splice(itemIndex, 1, response.data)
                    })
                    .catch((error) => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.update_failed') + ' ' + error.response.data['hydra:description'];
                        this.color = 'error';
                    });
            },
            dismissDiscountOnProduct(product) {
                let itemIndex = this.products.indexOf(product)
                this.$axios.post('products/' + product.id + '/discounts/dismiss', {})
                    .then((response) => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.product_saved');
                        this.color = 'success';
                        this.products.splice(itemIndex, 1, response.data)

                    })
                    .catch((error) => {
                        this.snackbar = true;
                        this.message = this.$i18n.t('product.update_failed') + ' ' + error.response.data['hydra:description'];
                        this.color = 'error';
                    });
            }
        },

        watch: {
            queryString: {
                handler() {
                    this.load();
                }
            },
        },

        computed: {
            ...mapState(['barColor', 'userInformation']),
            countPage: function () {
                return Math.ceil(this.totalItems / 30);
            },
            queryString: function () {
                let params = {
                    page: this.options.page, ...this.$route.params, ...this.$route.query, ...this.filters,
                    order: {}
                };
                return createQueryString(params);
            },
            queryStatus: function () {
                return this.$route.query['status[]'] || this.$route.query['status'] || [];
            },
        },
        components: {ProductsFilters}
    }
</script>

<style lang="scss" scoped>
    .v-card {
        max-width: 375px;
        margin: auto;
        .v-card__title {
            padding-bottom: 0;
        }
        a {
            text-decoration: none;
        }
    }

    #list-products .row::after {
        content: '';
        flex: auto;
    }

    .hidden-for-sale {
        opacity: 25%;
    }
</style>
